import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  Output,
  SimpleChange
} from '@angular/core'
import { ValidatorFn, Validators } from '@angular/forms'

import { CommonModule } from '@angular/common'
import { CaseInput } from '../../../interfaces/case-input.interface'
import { SelectOption } from '../../../interfaces/select-option.interface'

@Component({
    selector: 'case-multi-select-input',
    templateUrl: './multi-select-input.component.html',
    styleUrls: ['./multi-select-input.component.scss'],
    standalone: true,
    imports: [
      CommonModule
    ]
})
export class MultiSelectInputComponent implements CaseInput, OnChanges {
  @Input() label: string
  @Input() initialValue: string[] = []
  @Input() selectOptions: SelectOption[]
  @Input() placeholder = 'Select one or more items...'
  @Input() helpText: string
  @Input() itemNameSingular = 'item'
  @Input() itemNamePlural = 'items'
  @Input() readonly: boolean
  @Input() showErrors = false
  @Input() maxSelectedItems: number
  @Input() validators: ValidatorFn[] = []
  @Input() required: boolean
  @Input() uniqueId: string
  @Input() selectAllDisabled: boolean = false

  @Output() valueChanged: EventEmitter<string[]> = new EventEmitter()

  activeOptions: SelectOption[] = []
  showList = false

  constructor(private elementRef: ElementRef, private ngZone: NgZone) {}

  ngOnChanges(changes: { selectOptions?: SimpleChange }) {
    if (this.validators.includes(Validators.required)) {
      this.required = true
    }

    // Reset form value if we change select options.
    if (this.activeOptions.length && changes.selectOptions) {
      return this.selectNone()
    }

    if (this.initialValue) {
      this.activeOptions = this.initialValue.map((value) => {
        const option = this.selectOptions?.find(
          (selectOption) => String(selectOption.value) === String(value)
        )
        return { ...option, selected: true }
      })
    }

    if (this.selectOptions?.length) {
      this.selectOptions.forEach((selectOption: SelectOption) => {
        if (
          this.activeOptions.find(
            (activeOption) => activeOption.value === selectOption.value
          )
        ) {
          selectOption.selected = true
        }
      })
    }
  }

  toggleSelected(option: SelectOption): void {
    if (this.readonly) return

    const isSelected = this.activeOptions.includes(option)

    if (isSelected) {
      option.selected = false
      this.activeOptions = this.activeOptions.filter((o) => o !== option)
    } else if (
      !this.maxSelectedItems ||
      this.activeOptions.length < this.maxSelectedItems
    ) {
      option.selected = true
      this.activeOptions = [...this.activeOptions, option]
    }

    this.valueChanged.emit(
      this.activeOptions.map((option) => option.value.toString())
    )
  }

  selectAll() {
    this.ngZone.run(() => {
      this.selectOptions.forEach((option) => (option.selected = true))
      this.activeOptions = this.selectOptions
      this.valueChanged.emit(
        this.activeOptions.map((option) => option.value.toString())
      )
    })
  }

  selectNone() {
    this.ngZone.run(() => {
      this.selectOptions.forEach((option) => (option.selected = false))
      this.activeOptions = []
      this.valueChanged.emit(
        this.activeOptions.map((option) => option.value.toString())
      )
    })
  }

  // Click outside closes list
  @HostListener('document:click', ['$event.target'])
  clickOut(eventTarget) {
    if (
      this.showList &&
      !this.elementRef.nativeElement.contains(eventTarget) &&
      !eventTarget.className.includes('mass-selection-button')
    ) {
      this.showList = false
    }
  }
}
